/* global JQuery */
import { Card2, ContentCol2, ContentColSize2, ContentRow2, SidebarMenuItem2, SidebarMenuItemBadge2, SidebarMenuTree2 } from 'bambooo';
import moment from 'moment';
import { fromLonLat } from 'ol/proj';
import { Organization as OrganizationAPI } from '../../Api/Organization';
import { Species as SpeciesAPI } from '../../Api/Species';
import { Tours as ToursAPI } from '../../Api/Tours';
import { User as UserAPI } from '../../Api/User';
import { BaseMap } from '../../Map/BaseMap';
import { BathymetryLayer } from '../../Map/Layers/BathymetryLayer';
import { OsmBaseLayer } from '../../Map/Layers/OsmBaseLayer';
import { SightingPointsLayer } from '../../Map/Layers/SightingPointsLayer';
import { TourSightingRouteLayer } from '../../Map/Layers/TourSightingRouteLayer';
import { TourTrackLayer } from '../../Map/Layers/TourTrackLayer';
import { SightingMapObjectType } from '../../Map/Styles/SightingStyles';
import { UtilDistanceCoast } from '../../Utils/UtilDistanceCoast';
import { UtilLocation } from '../../Utils/UtilLocation';
import { BasePage } from '../BasePage';
import { Lang } from '../../Lang';
import { TOURS_PAGE_NAME } from '../PageNames';
import { TrackingChart } from './TrackingChart';
import { TransferModal } from './TransferModal';
export class ToursMap extends BasePage {
    constructor(tourId) {
        super();
        /**
         * page name
         * @protected
         */
        this._name = 'tour_map';
        /**
         * @protected
         */
        this._map = null;
        /**
         * @protected
         */
        this._track = null;
        /**
         * @protected
         */
        this._sightingRoutes = null;
        /**
         * @protected
         */
        this._points = null;
        /**
         * Separate vector layer for the brushed-range highlight, so the
         * underlying boat/sighting features stay rendered with their normal
         * styles while we toggle the overlay on/off.
         * @protected
         */
        this._highlight = null;
        /**
         * @protected
         */
        this._badge = null;
        /**
         * Chart above the map (count bars + speed line).
         * @protected
         */
        this._chart = null;
        /**
         * Reusable transfer modal (admins only).
         * @protected
         */
        this._transferModal = null;
        /**
         * Cached parsed positions for fast highlight repaint.
         * @protected
         */
        this._parsedPositions = [];
        /**
         * Currently brushed range, or null.
         * @protected
         */
        this._brushRange = null;
        /**
         * Card containing the action bar — used to rebuild after reload.
         * @protected
         */
        this._actionBar = null;
        /**
         * Pinned reload trigger (set during loadContent so action handlers can call it).
         * @protected
         */
        this._reload = null;
        this._tourId = tourId;
    }
    async unloadContent() {
        if (this._map) {
            this._map.disposePopover(true);
            this._map.unload();
            this._map = null;
        }
        this._track = null;
        this._sightingRoutes = null;
        this._points = null;
        this._highlight = null;
        this._chart = null;
        this._transferModal = null;
        this._parsedPositions = [];
        this._brushRange = null;
        this._actionBar = null;
        this._reload = null;
    }
    async loadContent() {
        var _a, _b, _c, _d, _e, _f;
        const menuItem = (_c = (_b = (_a = this._wrapper.getMainSidebar()) === null || _a === void 0 ? void 0 : _a.getSidebar().getMenu()) === null || _b === void 0 ? void 0 : _b.getMenuItem(TOURS_PAGE_NAME)) !== null && _c !== void 0 ? _c : null;
        let menuTree = null;
        const title = `Tour #${this._tourId}`;
        if (menuItem !== null) {
            menuTree = new SidebarMenuTree2(menuItem);
            const pmenuItem = new SidebarMenuItem2(menuTree, true);
            pmenuItem.setTitle(title);
            pmenuItem.setActiv(true);
            this._badge = new SidebarMenuItemBadge2(pmenuItem);
            this._badge.setContent('0');
        }
        const contentWrapper = this._wrapper.getContentWrapper().getContent();
        // chart card --------------------------------------------------------------------------------------------------
        const rowChart = new ContentRow2(contentWrapper.getContentFluidElement());
        const chartCol = new ContentCol2(rowChart, ContentColSize2.col12);
        const chartCard = new Card2({});
        chartCard.appendTo(chartCol.getElement());
        chartCard.setTitle(Lang.i().l('Tracking points · time / count / speed'));
        const chartTools = chartCard.getToolsElement();
        // Bucket-size select — default 10 minutes (user request).
        const bucketLabel = jQuery('<span class="text-muted small pr-1">Bucket</span>').appendTo(chartTools);
        bucketLabel.css('vertical-align', 'middle');
        const bucketSelect = jQuery('<select class="form-control form-control-sm d-inline-block" style="width: auto;"/>').appendTo(chartTools);
        for (const minutes of [1, 5, 10, 15, 30, 60]) {
            const opt = jQuery('<option/>').attr('value', `${minutes}`).text(`${minutes} min`);
            if (minutes === 10) {
                opt.attr('selected', 'selected');
            }
            bucketSelect.append(opt);
        }
        const chartHost = jQuery('<div class="tour-tracking-chart"/>').appendTo(chartCard.getBodyElement());
        this._chart = new TrackingChart(chartHost[0]);
        bucketSelect.on('change', () => {
            const minutes = parseInt(bucketSelect.val(), 10);
            if (Number.isFinite(minutes) && minutes > 0 && this._chart) {
                this._chart.setBucketMinutes(minutes);
            }
        });
        // map card ----------------------------------------------------------------------------------------------------
        const rowMap = new ContentRow2(contentWrapper.getContentFluidElement());
        const mapCol = new ContentCol2(rowMap, ContentColSize2.col12);
        const card = new Card2({});
        card.appendTo(mapCol.getElement());
        card.setTitle(title);
        this._map = new BaseMap(card.getBodyElement());
        /*
         * Fill the map to the viewport bottom by measuring the map card body's
         * own top (below the chart card above it). Adapts to the AdminLTE 4
         * chrome — the old fixed `window.height() - 420` offset left a gap under
         * the map once the page-title header was dropped. Window-offset fallback
         * if layout isn't ready.
         */
        const tmMapTop = (_e = (_d = card.getBodyElement()[0]) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect().top) !== null && _e !== void 0 ? _e : 0;
        this._map.setHeight(Math.max(320, tmMapTop > 0 ? window.innerHeight - tmMapTop - 16 : window.innerHeight - 420));
        this._map.load({
            initialCenterLonLat: [11.030, 47.739],
            initialZoom: 2.2
        });
        this._map.addLayer(new OsmBaseLayer());
        this._map.addLayer(new BathymetryLayer());
        this._track = new TourTrackLayer();
        this._sightingRoutes = new TourSightingRouteLayer();
        this._points = new SightingPointsLayer();
        this._highlight = new SightingPointsLayer();
        // BaseMap.addLayer is keyed by getName() — two SightingPointsLayer
        // instances would collide and the second would evict the first.
        // Give the highlight a distinct name so both stay registered.
        this._highlight.getName = () => 'tracking_highlight_layer';
        this._highlight.getTitle = () => 'Tracking selection';
        this._map.addLayer(this._track);
        this._map.addLayer(this._sightingRoutes);
        this._map.addLayer(this._highlight);
        this._map.addLayer(this._points);
        // Highlight needs to sit ABOVE the boat track (z=60) and the
        // per-sighting route lines (z=70) so the brushed dots are
        // actually visible, but BELOW the sighting markers (z=99) so
        // those stay clickable.
        const highlightOl = this._highlight.getOlLayer();
        if (highlightOl && typeof highlightOl.setZIndex === 'function') {
            highlightOl.setZIndex(80);
        }
        const currentuser = await UserAPI.getUserInfo();
        const isAdmin = ((_f = currentuser === null || currentuser === void 0 ? void 0 : currentuser.user) === null || _f === void 0 ? void 0 : _f.isAdmin) === true;
        let viewCenter = null;
        if (currentuser && currentuser.organization) {
            viewCenter = fromLonLat([
                parseFloat(currentuser.organization.lon),
                parseFloat(currentuser.organization.lat)
            ]);
        }
        // action bar (admin only) -------------------------------------------------------------------------------------
        if (isAdmin) {
            this._buildActionBar(chartCard.getBodyElement());
            this._transferModal = new TransferModal(contentWrapper);
        }
        // chart brush → highlight + action enable ---------------------------------------------------------------------
        this._chart.onBrush((range) => {
            this._brushRange = range;
            this._renderHighlight();
            this._refreshActionBar();
        });
        // reload pipeline ---------------------------------------------------------------------------------------------
        this._reload = async () => {
            await this._loadTrackingData(card, title, viewCenter);
        };
        await this._reload();
    }
    /**
     * Builds the inline Delete / Transfer button strip + status text.
     * Only called when the current user is an admin.
     */
    _buildActionBar(host) {
        const bar = jQuery('<div class="d-flex align-items-center pt-2" style="gap: 0.5rem;">' +
            '<div class="text-muted small flex-grow-1"></div>' +
            '<button type="button" class="btn btn-sm btn-outline-danger" disabled><i class="fas fa-trash"></i> Delete range</button>' +
            '<button type="button" class="btn btn-sm btn-outline-primary" disabled><i class="fas fa-exchange-alt"></i> Transfer range…</button>' +
            '</div>');
        host.append(bar);
        const info = bar.find('div.text-muted');
        const btnDelete = bar.find('button.btn-outline-danger');
        const btnTransfer = bar.find('button.btn-outline-primary');
        info.text(Lang.i().l('Brush a range on the chart to enable actions.'));
        btnDelete.on('click', () => {
            this._handleDelete();
        });
        btnTransfer.on('click', () => {
            this._handleTransfer();
        });
        this._actionBar = { info, btnDelete, btnTransfer };
    }
    /**
     * Re-renders the highlight layer based on `_brushRange` over `_parsedPositions`.
     */
    _renderHighlight() {
        if (!this._highlight) {
            return;
        }
        this._highlight.clearFeatures();
        if (!this._brushRange) {
            this._highlight.refresh();
            return;
        }
        const { timestampFrom, timestampTo } = this._brushRange;
        for (const pos of this._parsedPositions) {
            if (pos.timestamp === undefined || pos.longitude === undefined || pos.latitude === undefined) {
                continue;
            }
            if (pos.timestamp < timestampFrom || pos.timestamp > timestampTo) {
                continue;
            }
            this._highlight.addSighting(SightingMapObjectType.TrackingHighlight, `hl-${pos.timestamp}`, `Tracking @ ${moment(pos.timestamp).format('HH:mm:ss')}`, [pos.longitude, pos.latitude]);
        }
        this._highlight.refresh();
    }
    _selectedCount() {
        if (!this._brushRange) {
            return 0;
        }
        const { timestampFrom, timestampTo } = this._brushRange;
        let n = 0;
        for (const pos of this._parsedPositions) {
            if (pos.timestamp !== undefined &&
                pos.timestamp >= timestampFrom &&
                pos.timestamp <= timestampTo) {
                n++;
            }
        }
        return n;
    }
    _refreshActionBar() {
        if (!this._actionBar) {
            return;
        }
        const count = this._selectedCount();
        if (count === 0 || !this._brushRange) {
            this._actionBar.info.text(Lang.i().l('Brush a range on the chart to enable actions.'));
            this._actionBar.btnDelete.attr('disabled', 'disabled');
            this._actionBar.btnTransfer.attr('disabled', 'disabled');
            return;
        }
        const from = moment(this._brushRange.timestampFrom).format('HH:mm:ss');
        const to = moment(this._brushRange.timestampTo).format('HH:mm:ss');
        this._actionBar.info.text(`Selection: ${from} → ${to} · ${count} points`);
        this._actionBar.btnDelete.removeAttr('disabled');
        this._actionBar.btnTransfer.removeAttr('disabled');
    }
    async _handleDelete() {
        if (!this._brushRange) {
            return;
        }
        const count = this._selectedCount();
        // eslint-disable-next-line no-alert
        const ok = window.confirm(`Delete ${count} tracking point(s) in the brushed range? This cannot be undone.`);
        if (!ok) {
            return;
        }
        try {
            const deleted = await ToursAPI.deleteTracking(this._tourId, this._brushRange.timestampFrom, this._brushRange.timestampTo);
            this._brushRange = null;
            if (this._chart) {
                this._chart.clearBrush();
            }
            if (this._reload) {
                await this._reload();
            }
            // eslint-disable-next-line no-alert
            window.alert(`Deleted ${deleted} tracking points.`);
        }
        catch (e) {
            // eslint-disable-next-line no-alert
            window.alert(`Delete failed: ${e.message}`);
        }
    }
    async _handleTransfer() {
        if (!this._brushRange || !this._transferModal) {
            return;
        }
        const count = this._selectedCount();
        const range = this._brushRange;
        const neighbors = await ToursAPI.getTrackingNeighbors(this._tourId);
        this._transferModal.resetValues();
        this._transferModal.setRange(range.timestampFrom, range.timestampTo, count);
        this._transferModal.setNeighbors(neighbors === null || neighbors === void 0 ? void 0 : neighbors.prev, neighbors === null || neighbors === void 0 ? void 0 : neighbors.next);
        this._transferModal.setOnConfirm(async (targetTourId) => {
            var _a;
            try {
                const transferred = await ToursAPI.transferTracking(this._tourId, targetTourId, range.timestampFrom, range.timestampTo);
                (_a = this._transferModal) === null || _a === void 0 ? void 0 : _a.hide();
                this._brushRange = null;
                if (this._chart) {
                    this._chart.clearBrush();
                }
                if (this._reload) {
                    await this._reload();
                }
                // eslint-disable-next-line no-alert
                window.alert(`Transferred ${transferred} tracking points to tour #${targetTourId}.`);
            }
            catch (e) {
                // eslint-disable-next-line no-alert
                window.alert(`Transfer failed: ${e.message}`);
            }
        });
        this._transferModal.show();
    }
    /**
     * Loads tracking + species from the backend and rebuilds the map +
     * chart from scratch. Used both for the first paint and after every
     * delete/transfer.
     */
    async _loadTrackingData(card, title, defaultViewCenter) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (this._map) {
            this._map.setView(defaultViewCenter);
        }
        // species -----------------------------------------------------------------------------------------------------
        const species = await SpeciesAPI.getList();
        const mspecies = new Map();
        if (species) {
            for (const tspecies of species) {
                mspecies.set(tspecies.id, tspecies);
            }
        }
        const trackingData = await ToursAPI.getTrackingList(this._tourId);
        // Reset buffered features so reload doesn't duplicate them.
        if (this._track) {
            this._track.setTrack([]);
            this._track.refresh();
        }
        (_a = this._sightingRoutes) === null || _a === void 0 ? void 0 : _a.clearFeatures();
        (_b = this._points) === null || _b === void 0 ? void 0 : _b.clearFeatures();
        (_c = this._highlight) === null || _c === void 0 ? void 0 : _c.clearFeatures();
        (_d = this._highlight) === null || _d === void 0 ? void 0 : _d.refresh();
        this._parsedPositions = [];
        if (!trackingData) {
            (_e = this._chart) === null || _e === void 0 ? void 0 : _e.setData([], []);
            (_f = this._sightingRoutes) === null || _f === void 0 ? void 0 : _f.refresh();
            (_g = this._points) === null || _g === void 0 ? void 0 : _g.refresh();
            return;
        }
        const org = await OrganizationAPI.getOrganization(trackingData.org_id);
        if (org && this._map) {
            const orgViewCenter = fromLonLat([
                parseFloat(org.lon),
                parseFloat(org.lat)
            ]);
            this._map.setView(orgViewCenter);
        }
        const trackDate = moment((_h = trackingData.date) === null || _h === void 0 ? void 0 : _h.split(' ')[0]);
        card.setTitle(`${title} - <b>${trackDate.format('YYYY.MM.DD')}</b> - begin: ${trackingData.start} end: ${trackingData.end}`);
        const positionSort = new Map();
        const sighPostionTrack = new Map();
        if (trackingData.sightings.length > 0 && this._badge) {
            this._badge.setContent(`${trackingData.sightings.length}`);
        }
        for (const sighting of trackingData.sightings) {
            const sightPostionTrackData = {
                points: [],
                pointtype: sighting.pointtype,
                timestamp_start: 0,
                timestamp_end: 0,
                species_id: sighting.species_id,
                species_name: sighting.species_name,
                species_count: sighting.species_count,
                distance_coast: sighting.distance_coast,
                files: sighting.files,
                extended: sighting.extended
            };
            try {
                const posObjBegin = JSON.parse(sighting.location_begin);
                if (posObjBegin.timestamp) {
                    sightPostionTrackData.timestamp_start = posObjBegin.timestamp;
                }
            }
            catch (e) {
                console.log(e);
            }
            try {
                const posObjEnd = JSON.parse(sighting.location_end);
                if (posObjEnd.timestamp) {
                    sightPostionTrackData.timestamp_end = posObjEnd.timestamp;
                }
            }
            catch (e) {
                console.log(e);
            }
            sighPostionTrack.set(sighting.id, sightPostionTrackData);
        }
        for (const position of trackingData.positions) {
            try {
                const posObj = JSON.parse(position);
                if (posObj.timestamp) {
                    positionSort.set(posObj.timestamp, posObj);
                }
            }
            catch (e) {
                console.log(e);
            }
        }
        const positionListObj = [...positionSort].sort();
        const coordList = [];
        let index = 0;
        let start = null;
        let end = null;
        for (const [timestamp, value] of positionListObj) {
            if (index === 0) {
                start = value;
            }
            if (value.longitude && value.latitude) {
                for (const [sightingid, sighting] of sighPostionTrack) {
                    if (timestamp >= sighting.timestamp_start && timestamp <= sighting.timestamp_end) {
                        sighting.points.push([value.longitude, value.latitude]);
                    }
                    sighPostionTrack.set(sightingid, sighting);
                }
                coordList.push([value.longitude, value.latitude]);
                this._parsedPositions.push(value);
                if (index > 0 && (index % 400 === 0) && end && this._points) {
                    this._points.addRawObject({
                        type: 'Feature',
                        properties: {
                            pointtype: 'boat',
                            start: [end.longitude, end.latitude],
                            end: [value.longitude, value.latitude]
                        },
                        geometry: {
                            type: 'Point',
                            coordinates: [value.longitude, value.latitude]
                        }
                    });
                }
                end = value;
                index++;
            }
        }
        for (const [sightingId, sighting] of sighPostionTrack) {
            if (!sighting.points || !sighting.points[0] || (sighting.points[0].length !== 2)) {
                // eslint-disable-next-line no-continue
                continue;
            }
            if (this._sightingRoutes) {
                this._sightingRoutes.addSightingRoute(sighting.pointtype, sighting.points);
            }
            const speciesEntry = mspecies.get(sighting.species_id);
            let speciesName = '';
            if (speciesEntry) {
                speciesName = speciesEntry.name;
            }
            else {
                speciesName = sighting.species_name;
            }
            const floatDistance = parseFloat(sighting.distance_coast) || 0;
            let latStr = '';
            let lonStr = '';
            try {
                const lat = UtilLocation.ddToDm(sighting.points[0][1], true);
                latStr = `${lat.direction}: ${lat.degree}º ${lat.minute.toFixed(3)}`;
            }
            catch (e) {
                console.log(e);
            }
            try {
                const lon = UtilLocation.ddToDm(sighting.points[0][0], false);
                lonStr = `${lon.direction}: ${lon.degree}º ${lon.minute.toFixed(3)}`;
            }
            catch (e) {
                console.log(e);
            }
            let images = '';
            for (const file of sighting.files) {
                images += `<br><img width="200px" src="/json/sightings/getimage/${sightingId}/${file}" alt="${file}">`;
            }
            let speStartTimeStr = 'unknown';
            if (sighting.timestamp_start) {
                const speStartTime = moment(sighting.timestamp_start);
                speStartTimeStr = speStartTime.format('YYYY.MM.DD HH:mm:ss');
            }
            let extendedStr = '';
            for (const extended of sighting.extended) {
                switch (extended.name) {
                    case 'depth_contour':
                        extendedStr += `<b>Sea depth</b>: ${extended.data} m<br>`;
                        break;
                }
            }
            if (this._points) {
                this._points.addSighting(sighting.pointtype, sightingId, `<b>Species</b>: ${speciesName}<br>` +
                    `<b>Group-Size</b>: ${sighting.species_count}<br>` +
                    `<b>Distance (Miles)</b>: ${UtilDistanceCoast.meterToM(floatDistance, true)}<br>` +
                    `<b>Date/Time</b>: ${speStartTimeStr}<br>` +
                    `<b>Position</b>: ${latStr} - ${lonStr}<br>${images}<br>` +
                    `${extendedStr}`, sighting.points[0]);
            }
        }
        if (this._track) {
            this._track.setTrack(coordList);
            this._track.refresh();
        }
        if (start && this._points && start.longitude && start.latitude) {
            const startTime = moment(start.timestamp);
            this._points.addSighting(SightingMapObjectType.Start, 'start', `Tour-Start at: <b>${startTime.format('YYYY.MM.DD HH:mm:ss')}</b>`, [start.longitude, start.latitude]);
        }
        if (end && this._points && end.longitude && end.latitude) {
            const endTime = moment(end.timestamp);
            this._points.addSighting(SightingMapObjectType.End, 'end', `Tour-End at: <b>${endTime.format('YYYY.MM.DD HH:mm:ss')}</b>`, [end.longitude, end.latitude]);
        }
        (_j = this._sightingRoutes) === null || _j === void 0 ? void 0 : _j.refresh();
        (_k = this._points) === null || _k === void 0 ? void 0 : _k.refresh();
        // Feed the chart with the same data. Bucket size is whatever the
        // user picked on the toolbar.
        if (this._chart) {
            this._chart.setData(trackingData.positions, trackingData.sightings);
        }
        this._renderHighlight();
        this._refreshActionBar();
    }
}
